import React from 'react';

interface ChordReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
}

const CHORDS: { category: string; items: { chord: string; description: string }[] }[] = [
  {
    category: 'Node Addition',
    items: [
      { chord: 'a + s', description: 'Add Service node at cursor / next to selection' },
      { chord: 'a + d', description: 'Add Database node' },
      { chord: 'a + a', description: 'Add API Gateway' },
      { chord: 'a + p', description: 'Add Person (or Participant in sequence)' },
      { chord: 'a + q', description: 'Add Queue' },
      { chord: 'a + e', description: 'Add External' },
      { chord: 'a + y', description: 'Add System' },
      { chord: 'a + x', description: 'Add Decision' },
      { chord: 'a + i', description: 'Add Diamond' },
      { chord: 'a + g', description: 'Add Group node' },
      { chord: 'a + n', description: 'Add a typed note (sticky annotation)' },
      { chord: 'a + t', description: 'Arm typed-text once — click the canvas to type' },
      { chord: 'a + b', description: 'Add a generic box at the cursor (does not wait for a click)' },
      { chord: 'd + b', description: 'Arm Box once — click or drag on the canvas' },
      { chord: 'd d + b', description: 'Arm Box sticky — keeps drawing until Esc' },
      { chord: '2', description: 'Cycle Box: once → sticky → off' },
    ],
  },
  {
    category: 'Editing',
    items: [
      { chord: 'i', description: 'Tap: open info panel. Hold: inspect packed info, links, catalog' },
      { chord: 'r', description: 'Rename selected node (inline)' },
      { chord: 'Enter', description: 'Rename selected node (inline)' },
      { chord: 'Delete / Backspace', description: 'Delete selected items' },
      { chord: 'Cmd/Ctrl + Z', description: 'Undo' },
      { chord: 'Cmd/Ctrl + Shift + Z', description: 'Redo' },
      { chord: 'Cmd/Ctrl + C', description: 'Copy selected nodes' },
      { chord: 'Cmd/Ctrl + V', description: 'Paste nodes' },
      { chord: 'Cmd/Ctrl + X', description: 'Cut selected nodes' },
      { chord: 'Cmd/Ctrl + A', description: 'Select all nodes' },
      { chord: 'Shift + resize', description: 'Keep aspect ratio while dragging the corner' },
      { chord: 'Alt + resize', description: 'Scale drawing shapes from the center' },
      { chord: 'Ctrl + resize', description: 'Cascade size to snapped parent drawings' },
    ],
  },
  {
    category: 'Draw & connect',
    items: [
      { chord: 'd + shape', description: 'Arm a draw tool once (b box, t text, n note, o oval, r rounded, i diamond, c cylinder, h hex, a arrow, l line, f frame)' },
      { chord: 'd d + shape', description: 'Same tool, sticky' },
      { chord: 'click tool', description: 'Once → sticky → off. Dashed = once, solid = sticky' },
      { chord: 'E tap', description: 'Connect selected nodes, or arm click-two-nodes. Tap again to keep chaining' },
      { chord: 'hold E', description: 'Drag from a node body to another node to connect — no socket hunting' },
    ],
  },
  {
    category: 'Color & Style',
    items: [
      { chord: 'c + c + 0-9', description: 'Paint selection with a preset (0 slate … 3 yellow … 9 pink)' },
      { chord: 'c + c + c', description: 'Open the color picker' },
      { chord: 'c + c wait', description: 'Wait ~1.5s after c+c to open the picker (old habit)' },
      { chord: 't + a', description: 'Toggle animation on selected edges' },
      { chord: 't + e + a', description: 'Toggle animation on all edges' },
    ],
  },
  {
    category: 'Canvas & Navigation',
    items: [
      { chord: 't + f', description: 'Toggle distraction-free mode (hide all UI chrome)' },
      { chord: 'Space + drag', description: 'Pan canvas' },
      { chord: 'Scroll', description: 'Zoom in / out' },
      { chord: 'Shift + drag canvas', description: 'Marquee select (nodes and the actual line paths, not just the midpoint)' },
      { chord: 'Shift + Ctrl + drag canvas', description: 'Lasso select — freeform, auto-closes on release' },
      { chord: 'Cmd/Ctrl + [', description: 'Navigate back (page history)' },
      { chord: 'Cmd/Ctrl + ]', description: 'Navigate forward (page history)' },
      { chord: 'Cmd/Ctrl + K', description: 'Command palette — jump to page, node, catalog, or C4 workspace' },
      { chord: 'Tab', description: 'Enter selected group' },
      { chord: 'Escape', description: 'Exit group / cancel action' },
    ],
  },
  {
    category: 'Sidebar & Toolbox',
    items: [
      { chord: 't + p', description: 'Switch sidebar to Palette tab' },
      { chord: 't + i', description: 'Switch sidebar to Info tab' },
      { chord: 't + g', description: 'Switch sidebar to Pages tab' },
    ],
  },
  {
    category: 'Edge / Label',
    items: [
      { chord: 'Double-click edge', description: 'Edit edge label inline' },
      { chord: 'Enter', description: 'Confirm edge label' },
      { chord: 'Cmd/Ctrl + Enter  (Mac)', description: 'Insert newline in edge label' },
      { chord: 'Alt + Enter  (Windows/Linux)', description: 'Insert newline in edge label' },
      { chord: 'Escape', description: 'Cancel edge label edit' },
    ],
  },
  {
    category: 'Sequence Diagram',
    items: [
      { chord: 'a + p', description: 'Add participant with auto-cycling color' },
      { chord: 'Drag lifeline', description: 'Create a new message between participants' },
      { chord: 'i', description: 'Edit selected participant or message info' },
      { chord: 'c + c', description: 'Change color of selected participant or message' },
    ],
  },
  {
    category: 'Architecture grouping',
    items: [
      { chord: 'Cmd/Ctrl + G', description: 'Group selected nodes into an architecture subgraph (enter with Tab)' },
      { chord: 'Tab', description: 'Enter / exit architecture group' },
      { chord: 'b', description: 'Draw an annotation frame only — not a real architecture group' },
    ],
  },
  {
    category: 'Frame Drawing',
    items: [
      { chord: 'b', description: 'Start annotation-frame draw, then drag. Frames are visual-only.' },
    ],
  },
];

export function ChordReferenceModal({ isOpen, onClose, palette, glass }: ChordReferenceModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative z-10 w-[680px] max-h-[82vh] flex flex-col rounded-2xl shadow-2xl border overflow-hidden"
        style={{ ...glass }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: palette.panelBorder }}
        >
          <div>
            <span className="text-sm font-semibold" style={{ color: palette.panelText }}>⌨ Keyboard Shortcuts</span>
            <p className="text-xs mt-0.5" style={{ color: palette.panelSubText }}>Chords: press the first key, then the second within ~1.5s</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors text-sm"
            style={{ color: palette.panelSubText }}
          >✕</button>
        </div>

        {/* Body — two-column grid */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {CHORDS.map(section => (
              <div key={section.category}>
                <h3
                  className="text-[10px] font-bold uppercase tracking-widest mb-2"
                  style={{ color: '#818cf8' }}
                >
                  {section.category}
                </h3>
                <div className="space-y-1">
                  {section.items.map(item => (
                    <div key={item.chord} className="flex items-start gap-2">
                      <kbd
                        className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono border"
                        style={{
                          background: `${palette.panelBorder}30`,
                          borderColor: palette.panelBorder,
                          color: palette.panelText,
                          minWidth: 72,
                          textAlign: 'center',
                        }}
                      >
                        {item.chord}
                      </kbd>
                      <span className="text-xs leading-snug" style={{ color: palette.panelSubText }}>
                        {item.description}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 border-t text-center flex-shrink-0"
          style={{ borderColor: palette.panelBorder }}
        >
          <span className="text-[10px]" style={{ color: palette.panelSubText }}>
            Press <kbd className="px-1 rounded border text-[9px]" style={{ borderColor: palette.panelBorder }}>Escape</kbd> or click outside to close
            {' · '}Corner nudges appear when you click an action that has a shortcut
            {' · '}Tools → Take the tour
          </span>
        </div>
      </div>
    </div>
  );
}
